import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import LangLink from '../../components/common/LangLink';
import { langPath } from '../../utils/langPath';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import SEOHead from '../../components/common/SEOHead';
import AuthLayout, { AUTH_INPUT_CLASS, AUTH_BUTTON_CLASS } from './AuthLayout';

const ERROR_MESSAGES = {
    EMAIL_TAKEN:     { fr: 'Cette adresse email est déjà utilisée.', en: 'This email address is already in use.' },
    INVALID_DATA:    { fr: 'Données invalides.', en: 'Invalid data.' },
    SERVER_ERROR:    { fr: 'Erreur serveur. Veuillez réessayer plus tard.', en: 'Server error. Please try again later.' },
    NETWORK_ERROR:   { fr: 'Impossible de joindre le serveur. Vérifiez votre connexion.', en: 'Unable to reach the server. Check your connection.' },
    REGISTER_FAILED: { fr: "Échec de l'inscription.", en: 'Registration failed.' }
};

export default function Signup() {
    const [searchParams] = useSearchParams();
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        referralCode: searchParams.get('ref') || ''
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { register } = useAuth();
    const { language } = useLanguage();
    const navigate = useNavigate();
    const fr = language === 'fr';

    const t = (code) => ERROR_MESSAGES[code]?.[language] || ERROR_MESSAGES[code]?.fr || code;

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (formData.password !== formData.confirmPassword) {
            setError(fr ? 'Les mots de passe ne correspondent pas' : 'Passwords do not match');
            return;
        }
        if (formData.password.length < 6) {
            setError(fr ? 'Le mot de passe doit contenir au moins 6 caractères' : 'Password must be at least 6 characters');
            return;
        }
        setLoading(true);
        try {
            const result = await register(formData.email, formData.password, '', '', language, formData.referralCode.trim());
            if (result.success) navigate(langPath('/signup-success', language));
            else setError(t(result.errorCode || 'REGISTER_FAILED'));
        } catch (err) {
            setError(fr ? 'Une erreur est survenue. Veuillez réessayer.' : 'An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthLayout
            title={fr ? 'Créer un compte' : 'Create an account'}
            subtitle={fr ? "Commencez votre formation dès aujourd'hui" : 'Start your training today'}
            footer={
                <>
                    <p className="text-sm text-gray-500 dark:text-[#a9b1bd]">
                        {fr ? 'Vous avez déjà un compte ?' : 'Already have an account?'}{' '}
                        <LangLink to="/login" className="text-[#0061FE] dark:text-[#71a7ff] hover:text-[#0052db] dark:hover:text-white font-semibold transition">
                            {fr ? 'Se connecter' : 'Log in'}
                        </LangLink>
                    </p>
                    <LangLink to="/" className="block text-xs text-gray-500 dark:text-white/40 hover:text-gray-700 dark:hover:text-white/70 transition">
                        {fr ? '← Retour à l\'accueil' : '← Back to home'}
                    </LangLink>
                </>
            }
        >
            <SEOHead
                title={fr ? 'Inscription' : 'Sign Up'}
                description={fr ? 'Créez votre compte Nizus gratuitement.' : 'Create your free Nizus account.'}
                canonical="/signup"
                noindex
            />

            {error && (
                <div className="mb-5 p-3.5 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl">
                    <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label htmlFor="email" className="block text-xs font-semibold text-gray-700 dark:text-white/70 mb-2 uppercase tracking-wider">
                        {fr ? 'Adresse courriel' : 'Email address'}
                    </label>
                    <input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required
                        className={AUTH_INPUT_CLASS}
                        placeholder={fr ? 'vous@exemple.com' : 'you@example.com'} autoComplete="email" />
                </div>

                <div>
                    <label htmlFor="password" className="block text-xs font-semibold text-gray-700 dark:text-white/70 mb-2 uppercase tracking-wider">
                        {fr ? 'Mot de passe' : 'Password'}
                    </label>
                    <input id="password" name="password" type="password" value={formData.password} onChange={handleChange} required
                        className={AUTH_INPUT_CLASS}
                        placeholder="••••••••" autoComplete="new-password" />
                    <p className="mt-1.5 text-xs text-gray-500 dark:text-white/40">
                        {fr ? 'Minimum 6 caractères' : 'Minimum 6 characters'}
                    </p>
                </div>

                <div>
                    <label htmlFor="confirmPassword" className="block text-xs font-semibold text-gray-700 dark:text-white/70 mb-2 uppercase tracking-wider">
                        {fr ? 'Confirmer le mot de passe' : 'Confirm password'}
                    </label>
                    <input id="confirmPassword" name="confirmPassword" type="password" value={formData.confirmPassword} onChange={handleChange} required
                        className={AUTH_INPUT_CLASS}
                        placeholder="••••••••" autoComplete="new-password" />
                </div>

                <div>
                    <label htmlFor="referralCode" className="block text-xs font-semibold text-gray-700 dark:text-white/70 mb-2 uppercase tracking-wider">
                        {fr ? 'Code de parrainage' : 'Referral code'}
                        <span className="text-gray-500 dark:text-white/40 font-normal ml-1.5 normal-case tracking-normal">
                            ({fr ? 'optionnel' : 'optional'})
                        </span>
                    </label>
                    <input id="referralCode" name="referralCode" type="text" value={formData.referralCode} onChange={handleChange}
                        className={AUTH_INPUT_CLASS} placeholder="REF-XXXXX" />
                </div>

                <button type="submit" disabled={loading} className={`${AUTH_BUTTON_CLASS} mt-3`}>
                    {loading
                        ? (fr ? 'Création du compte...' : 'Creating account...')
                        : (fr ? 'Créer mon compte' : 'Create my account')}
                </button>
            </form>
        </AuthLayout>
    );
}
